import { Injectable } from '@angular/core';
import { UserService } from '@modules/auth/services/user.service';
import { combineLatest, distinctUntilChanged, filter, map, merge, Observable, race, startWith, take, timer } from 'rxjs';

import { PlansBundle } from '../models/plans.model';
import { PlansService } from './plans.service';

const FISCAL_PERMISSION = 'generate-fiscal-invoice';

/**
 * Tope de espera del gate de arranque. `plansBundle$` arranca en null y se llena tras el login;
 * si no llega (p. ej. falló `GET /plans`), se deniega en vez de colgar el alta de la caja fiscal.
 */
const GATE_WAIT_MS = 5000;
/** El POS espera este gate antes de emitir. Más corto que el arranque de sesión. */
export const FISCAL_EMIT_GATE_WAIT_MS = 3000;

/**
 * Acceso a facturación fiscal (ADM-828). Predicados sobre `user$` y `plansBundle$`:
 * no pide nada ni guarda un resultado propio.
 *
 * `hasFiscalAccess`: permiso `FISCAL_INVOICE` del plan y `bundle.fiscalReady === true`.
 * Si `fiscalReady` no viene (campo ausente, no `false`), vale solo el permiso del plan.
 * Ese fallback es transitorio. Para pantallas de configuración y avisos.
 *
 * `canIssueFiscal`: lo anterior y el permiso de usuario `generate-fiscal-invoice`. Para emitir.
 */
@Injectable({ providedIn: 'root' })
export class FiscalAccessService {
    constructor(private userService: UserService, private plansService: PlansService) {}

    hasFiscalAccess(): boolean {
        return this.accessFrom(this.plansService.plansBundle$.value);
    }

    canIssueFiscal(): boolean {
        return this.issueFrom(this.userService.user$.value, this.plansService.plansBundle$.value);
    }

    /** Emite de nuevo cuando cambia el usuario o el bundle. Para componentes OnPush que avisan. */
    hasFiscalAccess$(): Observable<boolean> {
        return this.changes$(() => this.hasFiscalAccess());
    }

    /**
     * Igual que `canAccessChanges$` de `AssistantAccessService`: `merge(user$, plansBundle$)`.
     * La primera emisión puede ser false con el bundle todavía en null. El arranque no debe
     * quedarse con ese valor: usa `canIssueFiscalWhenReady$`.
     */
    canIssueFiscal$(): Observable<boolean> {
        return this.changes$(() => this.canIssueFiscal());
    }

    /**
     * Espera usuario y bundle no nulos y entonces decide. Si no llegan a tiempo, deniega.
     * Para `FiscalSellerStorageService`, que corre al iniciar sesión.
     */
    canIssueFiscalWhenReady$(waitMs = GATE_WAIT_MS): Observable<boolean> {
        return this.ready$(waitMs, true);
    }

    /** Igual que el de emisión, pero solo acceso del comercio (configuración, no el permiso de usuario). */
    hasFiscalAccessWhenReady$(waitMs = GATE_WAIT_MS): Observable<boolean> {
        return this.ready$(waitMs, false);
    }

    /**
     * Por qué `canIssueFiscal()` es false. `loading` si todavía no hay usuario o bundle:
     * no es lo mismo que faltarle el permiso a la persona.
     */
    issueDenial(): 'ok' | 'loading' | 'user' | 'commerce' {
        const user = this.userService.user$.value;
        const bundle = this.plansService.plansBundle$.value;
        if (!user || !bundle) return 'loading';
        if (!this.userAllows(user)) return 'user';
        if (!this.accessFrom(bundle)) return 'commerce';
        return 'ok';
    }

    private ready$(waitMs: number, requireUser: boolean): Observable<boolean> {
        const user$ = this.userService.user$.pipe(
            filter(user => !!user),
            take(1),
        );
        const bundle$ = this.plansService.plansBundle$.pipe(
            filter(bundle => !!bundle),
            take(1),
        );
        const decision$ = requireUser
            ? combineLatest([user$, bundle$]).pipe(
                  take(1),
                  map(([user, bundle]) => this.issueFrom(user, bundle)),
              )
            : bundle$.pipe(
                  take(1),
                  map(bundle => this.accessFrom(bundle)),
              );

        return race(decision$, timer(waitMs).pipe(map(() => false)));
    }

    private changes$(read: () => boolean): Observable<boolean> {
        return merge(this.userService.user$, this.plansService.plansBundle$).pipe(
            map(() => read()),
            startWith(read()),
            distinctUntilChanged(),
        );
    }

    private accessFrom(bundle: PlansBundle | null): boolean {
        if (!bundle) return false;
        const planAllows = this.planAllows(bundle);
        // Campo ausente (no `false`): el comportamiento de hoy, solo el permiso del plan.
        if (bundle.fiscalReady === undefined) return planAllows;
        return planAllows && bundle.fiscalReady === true;
    }

    private issueFrom(user: unknown, bundle: PlansBundle | null): boolean {
        return this.accessFrom(bundle) && this.userAllows(user);
    }

    private planAllows(bundle: PlansBundle | null): boolean {
        const planPerms = bundle?.currentPlan?.permissions?.FISCAL_INVOICE;
        return Array.isArray(planPerms) && planPerms.includes(FISCAL_PERMISSION);
    }

    private userAllows(user: unknown): boolean {
        const userPerms = (user as { permissions?: { FISCAL_INVOICE?: unknown } } | null)?.permissions?.FISCAL_INVOICE;
        return Array.isArray(userPerms) && userPerms.includes(FISCAL_PERMISSION);
    }
}
